/**
 * Loading screen that displays while order state is being loaded after a
 * payment redirect.
 *
 * The purpose of this view is to smooth over the payment flow for payment processors
 * that redirect the browser.
 *
 * Usually, when the app starts up, the session state (order, customer data) is
 * loaded in the background without an indication to the customer. This is because
 * the app has partial functionality without session state (menu views). However,
 * some payment processors will redirect the browser to a different domain to
 * complete the payment and redirect back to the storefront when done. When the
 * storefront reloads, the lack of indication to customers that their payment is
 * continuing while the session state is being loaded induces anxiety.
 *
 * To solve this, this view adds a loading screen between when the app is ready
 * and when the session state is loaded. The session state will
 * contain the outcome of the payment. A switch to enable/disable the loading
 * screen is stored with a cookie avoid needing to wait for the order state to load.
 * This switch is enabled when an online payment starts.
 */
import React from 'react';

import LoadingModal from './loading-modal';

const { Order, UI } = Mobi2Go;
const { cookie } = Mobi2Go.helpers;
const COOKIE_KEY = 'MOBI2GO_LOADING_SCREEN_ON_REFRESH';

function enableLoadingScreenOnNextLoad() {
    cookie.set(COOKIE_KEY, 'enabled', 60 * 30);
}

function disableLoadingScreenOnNextLoad() {
    cookie.set(COOKIE_KEY, 'disabled', 1);
}

function shouldShowLoadingScreenUntilStateReady() {
    return cookie.get(COOKIE_KEY) === 'enabled';
}

// Display loading screen on app load if necessary
Mobi2Go.onReady(() => {
    if (!shouldShowLoadingScreenUntilStateReady()) {
        return;
    }

    const modal = UI.ExternalModal.create(
        'order-payment-redirect-loading-modal',
        <LoadingModal />,
        {
            'data-view': 'order-payment-redirect-loading-modal',
            className: 'm2g-order-payment-redirect-loading-modal',
        }
    );

    modal.mount();

    modal.open();
    modal.setIsClosable(false);
    disableLoadingScreenOnNextLoad();

    Order.onReady(() => {
        modal.setIsClosable(true);
        modal.close();
    });
});

// Enable loading screen when an online payment starts
Order.bind('updateProgressPhase', () => {
    if (
        Order.getProgressPhase() === 'confirming' &&
        Order.getPaymentMethod() === 'online' &&
        Order.getPaymentProcessor() &&
        (Order.getPaymentProcessor().form_type === 'direct' ||
            Order.getPaymentProcessor().form_type === 'redirect')
    ) {
        enableLoadingScreenOnNextLoad();
    } else {
        disableLoadingScreenOnNextLoad();
    }
});
